/**
 * @file Onglet Coffre : tirages normal/epic (x1/x10), arme gratuite du jour, fusion auto, grille des armes avec glisser-deposer (doigt ET souris).
 */
import type { GachaPoolId, WalletAmounts } from '@config/types';
import type { WeaponItemView } from '@core/index';
import { dayKey } from '@core/index';
import { Decimal } from '@shared/bignum';
import type { WalletId } from '@shared/enums';
import type { Uid } from '@shared/types';
import { Component } from '../../core/Component';
import { h, setText, toggleClass } from '../../core/dom';
import { send } from '../../core/feedback';
import { amountChip, balanceOf, reconcile, walletChips } from '../../core/widgets';
import type { UiContext } from '../../UiContext';
import type { WeaponDrag } from './drag';
import { createTile } from './tile';
import type { TileParts } from './tile';

/** Contrat avec l'ecran : selection partagee avec les emplacements, equipement, menu contextuel. */
export interface WeaponsShared {
  selected(): Uid | null;
  select(uid: Uid | null): void;
  equip(uid: Uid, slot: number): void;
  /** Premier emplacement debloque libre, sinon le dernier debloque. */
  autoSlot(): number;
  /** Glisser-deposer commun au coffre et aux emplacements. */
  drag: WeaponDrag;
  /** Menu contextuel d'une arme (verrou, equiper / retirer). */
  openMenu(uid: Uid, anchor: HTMLElement): void;
}

const POOLS: readonly GachaPoolId[] = ['normal', 'epic'];

/** Montants multiplies par `n`, sous forme de paires (id, Decimal). */
const scaled = (amounts: WalletAmounts, n: number): Array<[WalletId, Decimal]> =>
  (Object.keys(amounts) as WalletId[]).flatMap((id) => {
    const v = amounts[id];
    return v === undefined ? [] : [[id, Decimal.fromValue(v).times(n)] as [WalletId, Decimal]];
  });

export class InventoryPane extends Component {
  private readonly tiles = new Map<string, HTMLElement>();
  private readonly parts = new WeakMap<HTMLElement, TileParts>();
  private grid!: HTMLElement;
  private chestText!: HTMLElement;
  private buySlot!: HTMLElement;
  private buySlotCost!: HTMLElement;
  private autoToggle!: HTMLElement;
  private mergeAll!: HTMLElement;
  private autoRow!: HTMLElement;
  private dailyBtn!: HTMLElement;
  private readonly drawBtns: Array<{ pool: GachaPoolId; count: number; root: HTMLElement }> = [];
  private lastFirst: HTMLElement | null = null;
  private popUid: Uid | null = null;

  constructor(
    ctx: UiContext,
    private readonly shared: WeaponsShared,
  ) {
    super(ctx, 'div', 'inv');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    const { gacha } = this.ctx.config.balance;

    // ---- tirages
    const draws = h('div', { class: 'draws' });
    // Tirages UN PAR UN, cote a cote (le core garde le x10 pour d'autres usages, l'UI ne le propose plus).
    for (const pool of POOLS) {
      const spec = gacha[pool];
      for (const count of [1]) {
        const label = t.t(pool === 'epic' ? 'weapons.drawEpic' : 'weapons.drawNormal');
        const root = h(
          'button',
          {
            class: `btn draw draw-${pool}${count === 1 ? '' : ' multi'}`,
            type: 'button',
            'data-ui-block': true,
            'data-tutorial': pool === 'normal' && count === 1 ? 'draw-normal' : undefined,
            onclick: () => void send(this.ctx, { type: 'weapon.draw', pool, count }),
          },
          h('span', { class: 'draw-label' }, label),
          h('span', { class: 'draw-cost' }, ...scaled(spec.cost, count).map(([id, v]) => amountChip(id, v))),
        );
        this.drawBtns.push({ pool, count, root });
        draws.append(root);
      }
    }
    this.dailyBtn = h('button', { class: 'btn gem daily-draw is-hidden', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'weapon.dailyFreeDraw' }) }, t.t('weapons.dailyFree'));

    // ---- coffre
    this.chestText = h('b', { class: 'chest-count' });
    this.buySlotCost = h('span', { class: 'chips-inline' });
    this.buySlot = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'weapon.buyChestSlot' }) }, t.t('weapons.buySlot'), this.buySlotCost);
    this.autoToggle = h('button', { class: 'btn small toggle-chip', type: 'button', role: 'switch', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'weapon.setAutoMerge', enabled: !this.ctx.sim.data.weapons.autoMerge }) }, t.t('weapons.autoMerge'));
    this.mergeAll = h('button', { class: 'btn small', type: 'button', 'data-ui-block': true, onclick: () => void send(this.ctx, { type: 'weapon.autoMerge' }) }, t.t('weapons.mergeAll'));
    this.autoRow = h('div', { class: 'btn-row' }, this.autoToggle, this.mergeAll);
    this.grid = h('div', { class: 'chest-grid' });

    this.el.append(draws, this.dailyBtn, h('div', { class: 'chest-head' }, this.chestText, this.buySlot), this.autoRow, h('p', { class: 'hint' }, t.t('weapons.mergeHint')), this.grid);

    // zone de depot « coffre » : y lacher une arme d'un emplacement la retire
    this.grid.dataset.chest = '';
    // etat vide (CSS :empty::before) : guide le joueur vers les tirages plutot qu'une zone blanche muette
    this.grid.dataset.empty = t.t('weapons.chestEmpty');
    this.listen('WeaponsMerged', (e) => {
      this.popUid = e.result;
    });
    this.update(0);
  }

  // ------------------------------------------------------------------------------------------------ tuiles

  private createItem(v: WeaponItemView): HTMLElement {
    const p = createTile(this.ctx, v.def);
    p.root.dataset.uid = v.item.uid;
    this.parts.set(p.root, p);
    this.shared.drag.attach(p.root, () => ({ uid: v.item.uid, slot: null, el: p.root, draggable: true }));
    p.root.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this.shared.openMenu(v.item.uid, p.root);
    });
    p.root.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.tap(v.item.uid);
      }
    });
    return p.root;
  }

  private updateItem(el: HTMLElement, v: WeaponItemView): void {
    const p = this.parts.get(el);
    if (!p) return;
    toggleClass(p.lock, 'is-hidden', !v.item.locked);
    toggleClass(p.merge, 'is-hidden', !v.mergeable);
    toggleClass(p.slot, 'is-hidden', true);
    toggleClass(el, 'equipped', false);
    toggleClass(el, 'locked', v.item.locked);
    toggleClass(el, 'selected', this.shared.selected() === v.item.uid);
  }

  // ------------------------------------------------------------------------------------------------ interactions

  private tap(uid: Uid): void {
    this.shared.select(this.shared.selected() === uid ? null : uid);
  }

  // ------------------------------------------------------------------------------------------------ rafraichissement

  override update(_dt: number): void {
    const { query, data } = this.ctx.sim;
    const { i18n, config } = this.ctx;
    const all = query.weapons();
    // Les armes equipees vivent dans les emplacements : la grille ne montre QUE le coffre (armes non equipees).
    const views = all.filter((v) => v.equippedSlot === null);
    reconcile(
      this.grid,
      this.tiles,
      views,
      (v) => v.item.uid,
      (v) => this.createItem(v),
      (el, v) => this.updateItem(el, v),
    );

    // Premiere vignette = cible du tutoriel.
    const first = this.grid.firstElementChild as HTMLElement | null;
    if (first !== this.lastFirst) {
      this.lastFirst?.removeAttribute('data-tutorial');
      first?.setAttribute('data-tutorial', 'inventory-first');
      this.lastFirst = first;
    }
    if (this.popUid) {
      const el = this.tiles.get(this.popUid);
      if (el) {
        el.classList.remove('pop');
        void el.offsetWidth;
        el.classList.add('pop');
        this.popUid = null;
      }
    }

    // Coffre : capacite sur les armes NON equipees.
    const count = views.length;
    const cap = config.balance.chest.freeSlots + data.weapons.chestPaidSlots;
    setText(this.chestText, i18n.t('weapons.chest', { count, cap }));
    toggleClass(this.chestText, 'full', count >= cap);
    const nextCost = config.balance.chest.paidSlotCost[data.weapons.chestPaidSlots];
    toggleClass(this.buySlot, 'is-hidden', nextCost === undefined);
    if (nextCost !== undefined) {
      const key = JSON.stringify(nextCost);
      if (this.buySlotCost.dataset.key !== key) {
        this.buySlotCost.dataset.key = key;
        this.buySlotCost.replaceChildren(...walletChips(this.ctx, nextCost));
      }
    }

    const autoMergeUnlocked = query.feature('autoMerge');
    toggleClass(this.autoRow, 'is-hidden', !autoMergeUnlocked);
    toggleClass(this.autoToggle, 'on', data.weapons.autoMerge);
    this.autoToggle.setAttribute('aria-checked', String(data.weapons.autoMerge));

    toggleClass(this.dailyBtn, 'is-hidden', data.weapons.dailyFreeDrawDay === dayKey(Date.now()));

    for (const b of this.drawBtns) {
      const spec = config.balance.gacha[b.pool];
      const poor = scaled(spec.cost, b.count).some(([id, v]) => balanceOf(this.ctx, id).lt(v));
      toggleClass(b.root, 'disabled', poor);
    }
  }
}
